import { formatInTimeZone, fromZonedTime } from "date-fns-tz"

const operationalZone = "America/New_York"
const operationalPattern = "yyyy-MM-dd'T'HH:mm"

/** Date-only and datetime-local values are wall-clock strings, never UTC instants. */
export function parseWallDate(value: string, type: "date" | "time" | "datetime-local"): Date | undefined {
  if (!value) return undefined
  const source = type === "time" ? `2000-01-01T${value}` : type === "date" ? `${value}T12:00` : value
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(source)
  if (!match) return undefined
  const [, year, month, day, hour, minute, second = "0"] = match
  if (+year < 100 || +month < 1 || +month > 12 || +day < 1 || +hour > 23 || +minute > 59 || +second > 59) return undefined
  const date = new Date(+year, +month - 1, +day, +hour, +minute, +second)
  return date.getFullYear() === +year && date.getMonth() === +month - 1 && date.getDate() === +day ? date : undefined
}
export function formatWallDate(date: Date, type: "date" | "time" | "datetime-local"): string {
  const pad = (value: number) => String(value).padStart(2, "0")
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`
  return type === "date" ? day : type === "time" ? time : `${day}T${time}`
}

/** A stored instant shown to dispatch always uses Eastern time, not the browser zone. */
export function operationalInputValue(value?: string | Date | null, part: "date" | "time" | "datetime-local" = "datetime-local"): string {
  if (!value) return ""
  const instant = new Date(value)
  if (!Number.isFinite(instant.getTime())) return ""
  return formatInTimeZone(instant, operationalZone, part === "date" ? "yyyy-MM-dd" : part === "time" ? "HH:mm" : operationalPattern)
}

/** Matches the server's rejection of skipped/repeated Eastern daylight-saving times. */
export function operationalInstant(value: string): string {
  const invalid = () => new Error("Choose a valid, unambiguous Eastern date and time. This time may fall in a daylight-saving change.")
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) throw invalid()
  const instant = fromZonedTime(value, operationalZone)
  if (!Number.isFinite(instant.getTime()) || formatInTimeZone(instant, operationalZone, operationalPattern) !== value) throw invalid()
  // New York's repeated fall-back wall hour has two instants one hour apart.
  if ([-3600000, 3600000].some(offset => formatInTimeZone(new Date(instant.getTime() + offset), operationalZone, operationalPattern) === value)) throw invalid()
  return instant.toISOString()
}
